// ─── PART 3: SIGNAL BOARD — convergence engine ────────────────────────────────
// Dependencies: part1_core (C, s, fmt, bps, pct, AnalystButton)
// Exports: SignalBoard
//
// Receives globalSignals object from root App via onData callbacks.
// Each tab pushes its live data into globalSignals as it loads.
// SignalBoard runs rules engine on every globalSignals update.
//
// globalSignals shape:
//   vix           { current, history[], min52, max52 }
//   skew          number
//   pcRatio       { volumeRatio, oiRatio, callVol, putVol, callOI, putOI }
//   yieldSpread   number  (10Y - 2Y in decimal, e.g. -0.004)
//   petroleum     [{ date, value }]  (millions of barrels)
//   tankers       [{ name, type, speed, dest, aisDark, signal }]
//   fires         [{ type, frp, label, lat, lon }]
//   congressTrades [{ politician, ticker, type (BUY|SELL|…), filedDate, source }]
//   unusualOptions [{ strike, volume, oi, ratio, isCall, ticker }]
//   contracts     [{ company, agency, amount }]
//
// Signal object shape:
//   id         string   — unique, used as React key
//   strength   STRONG | ELEVATED | MODERATE | WEAK | NONE
//   type       string   — signal category label
//   color      C.*      — accent color
//   icon       string   — emoji
//   title      string
//   desc       string   — plain language, precise, no filler
//   sources    string[] — data sources that triggered it
//   sector     string   — affected sector
//   context    string   — pre-built analyst prompt string

import { useState, useEffect, useRef } from "react";
import { C, s, fmt, bps, pct, fmtK, AnalystButton } from "./core";
import { FactorBacktest } from "./FactorBacktest";
import { IREducationPanel } from "./IREducationPanel";

// ─── SIGNAL FACTOR SENSITIVITY MAP ────────────────────────────────────────────
// Static, display-only reference: which portfolio factors each signal type loads
// on, plus a one-line rationale. Keyed by the canonical signal-type ids used
// throughout the Factor Framework (see SIGNAL_ID_TO_TYPE / FACTOR_CONTEXT below);
// a raw rule id resolves to one of these via SIGNAL_ID_TO_TYPE. Factor names
// match globalSignals.portfolioExposures keys exactly (beta, momentum,
// volatility, value, quality, size). Detection and rendering are unchanged.
const SIGNAL_FACTOR_MAP = {
  "vix-skew-divergence": {
    factors: ["beta", "volatility"],
    rationale: "Volatility-regime divergence raises beta and volatility factor sensitivity as tail-risk demand builds.",
  },
  "vix-fear": {
    factors: ["beta", "volatility"],
    rationale: "Elevated fear widens dispersion, amplifying beta and volatility factor exposure.",
  },
  "yield-curve-inversion": {
    factors: ["value", "quality"],
    rationale: "Late-cycle risk-off rotation favors value and quality over high-beta growth.",
  },
  "monetary-regime": {
    factors: ["value", "momentum"],
    rationale: "Inflationary money-growth regime rewards value and sustains momentum in hard assets.",
  },
  "fiscal-dominance": {
    factors: ["value", "momentum"],
    rationale: "Constrained Fed policy pressures duration, tilting returns toward value and momentum.",
  },
  "dark-pool-defended": {
    factors: ["beta"],
    rationale: "Institutional level defense acts on the underlying's beta sensitivity most directly.",
  },
  "squeeze": {
    factors: ["momentum", "volatility"],
    rationale: "Forced covering drives momentum alongside a sharp volatility expansion.",
  },
  "pc-put-heavy": {
    factors: ["momentum", "volatility"],
    rationale: "Heavy put flow reflects directional and volatility positioning, loading on momentum and volatility.",
  },
  "ais-dark": {
    factors: ["momentum"],
    rationale: "Supply-disruption flow propagates through momentum in affected commodity names.",
  },
  "tanker-dark-cluster": {
    factors: ["momentum"],
    rationale: "Clustered dark vessels signal supply momentum across energy and shipping names.",
  },
  "petroleum-draw": {
    factors: ["momentum", "value"],
    rationale: "Sustained draws tighten supply, feeding energy momentum and value.",
  },
  "thermal-industrial": {
    factors: ["momentum"],
    rationale: "Operational-disruption heat signatures translate into sector momentum moves.",
  },
  "congress-trade": {
    factors: ["quality"],
    rationale: "Informed insider concentration correlates with quality-factor names.",
  },
  "sentiment-extreme": {
    factors: ["momentum", "beta"],
    rationale: "Contrarian reversals at sentiment extremes shift momentum and beta sensitivity.",
  },
};

// ─── SIGNAL STRENGTH COLORS ───────────────────────────────────────────────────
const STRENGTH_COLOR = {
  STRONG:   C.red,
  ELEVATED: C.orange,
  MODERATE: C.amber,
  WEAK:     C.textMid,
  NONE:     C.textDim,
};

// Ordinal rank of a strength label — used only as the RELEVANCE-sort tiebreaker.
const STRENGTH_RANK = { STRONG: 4, ELEVATED: 3, MODERATE: 2, WEAK: 1, NONE: 0 };

// ─── RULES ENGINE ─────────────────────────────────────────────────────────────
// Each rule is a function that receives globalSignals and returns a signal
// object or null. Rules are evaluated in order. Multiple rules can fire.
// Threshold rationale documented inline.

function ruleVixSkewDivergence({ vix, skew }) {
  // `skew` now carries VXVCLS (CBOE VIX3M, 3-month implied vol) — NOT the old CBOE
  // SKEW index (which traded ~135+). So this is a VIX term-structure rule. Healthy
  // markets are in contango: VIX < VIX3M, ratio ~0.85–0.95. As near-term stress is
  // priced in, the ratio flattens toward — and past — 1.0 (backwardation). That
  // flattening/inversion is the documented pre-/in-vol-expansion regime the old
  // SKEW threshold was meant to catch.
  if (!vix || vix.current == null || !skew || skew <= 0) return null;
  const ratio = vix.current / skew;               // VIX / VIX3M
  if (ratio < 0.95) return null;                   // healthy contango — no signal
  const inverted = vix.current > skew;             // full backwardation
  return {
    id:       "vix-skew-div",
    strength: inverted ? "STRONG" : "ELEVATED",
    type:     "DIVERGENCE",
    color:    inverted ? C.red : C.amber,
    icon:     "⚠",
    title:    "VIX TERM-STRUCTURE DIVERGENCE",
    desc:     inverted
      ? `VIX at ${fmt(vix.current)} has risen ABOVE 3-month VIX3M at ${fmt(skew, 1)} — the term structure is inverted (ratio ${fmt(ratio, 2)}). Near-term implied vol is now priced above the 3-month, backwardation that marks acute stress and typically accompanies or immediately precedes a vol expansion.`
      : `VIX at ${fmt(vix.current)} is closing on 3-month VIX3M at ${fmt(skew, 1)} (ratio ${fmt(ratio, 2)} vs normal contango ~0.85–0.95). The term structure is flattening toward inversion — the market is starting to price near-term stress against the 3-month, a documented pre-vol-expansion regime.`,
    sources:  ["CBOE VIX", "CBOE VIX3M (VXVCLS)"],
    sector:   "MACRO",
  };
}

function ruleVixFear({ vix }) {
  if (!vix) return null;
  // VIX above 30 = high fear. Options expensive board-wide.
  // Mean-reversion tendency but can persist in genuine crisis.
  if (vix.current > 30) {
    return {
      id:       "vix-fear",
      strength: "ELEVATED",
      type:     "FEAR REGIME",
      color:    C.orange,
      icon:     "🔥",
      title:    "ELEVATED FEAR REGIME",
      desc:     `VIX at ${fmt(vix.current)} — options are expensive across expirations and strikes. Mean-reversion tendency above 30 is well-documented but can persist in genuine systemic events. 52-week range: ${fmt(vix.min52)}–${fmt(vix.max52)}. Watch VIX term structure for contango collapse (signals acute stress) vs normal backwardation.`,
      sources:  ["CBOE VIX"],
      sector:   "MACRO",
    };
  }
  return null;
}

function ruleVixComplacency({ vix }) {
  if (!vix) return null;
  // VIX below 12 = extreme complacency. Historically precedes vol spikes.
  if (vix.current < 12) {
    return {
      id:       "vix-complacent",
      strength: "MODERATE",
      type:     "COMPLACENCY",
      color:    C.amber,
      icon:     "😶",
      title:    "EXTREME VOL COMPLACENCY",
      desc:     `VIX at ${fmt(vix.current)} — historically low. Market participants are pricing in very little uncertainty. VIX below 12 is in the bottom decile of readings since 1990. Low vol environments tend to be self-reinforcing until they aren't — often the setup for a sharp spike when a catalyst emerges.`,
      sources:  ["CBOE VIX"],
      sector:   "MACRO",
    };
  }
  return null;
}

function ruleYieldCurve({ yieldSpread }) {
  if (yieldSpread == null) return null;
  if (yieldSpread < 0) {
    // Inversion depth matters. Below -50bps is historically more significant.
    const depth = Math.abs(yieldSpread * 100);
    const severity = depth > 50 ? "STRONG" : "MODERATE";
    return {
      id:       "yield-inverted",
      strength: severity,
      type:     "MACRO",
      color:    C.red,
      icon:     "⊘",
      title:    "YIELD CURVE INVERTED",
      desc:     `2Y–10Y spread at ${bps(yieldSpread)} — inverted by ${fmt(depth, 0)}bps. Every U.S. recession since 1955 was preceded by inversion with a 6–18 month lead time. Depth matters: inversions beyond 50bps have historically signaled more severe downturns. Monitor for un-inversion (curve steepening from inversion) as a potential recession confirmation event, not relief.`,
      sources:  ["FRED — Treasury Yields"],
      sector:   "MACRO / CREDIT",
    };
  }
  if (yieldSpread < 0.005 && yieldSpread >= 0) {
    return {
      id:       "yield-flat",
      strength: "MODERATE",
      type:     "MACRO",
      color:    C.amber,
      icon:     "◈",
      title:    "YIELD CURVE NEAR-FLAT",
      desc:     `2Y–10Y spread at ${bps(yieldSpread)} — approaching flat. Near-zero spread signals economic deceleration concern without full inversion. Direction over the next 30 days is the key watch — further flattening vs steepening divergence.`,
      sources:  ["FRED — Treasury Yields"],
      sector:   "MACRO",
    };
  }
  return null;
}

function rulePutCallRatio({ pcRatio }) {
  if (!pcRatio) return null;
  const vr = parseFloat(pcRatio.volumeRatio);
  if (isNaN(vr)) return null;

  if (vr > 1.3) {
    return {
      id:       "pc-put-heavy",
      strength: "MODERATE",
      type:     "OPTIONS FLOW",
      color:    C.red,
      icon:     "📉",
      title:    "HEAVY PUT VOLUME",
      desc:     `Put/call volume ratio at ${pcRatio.volumeRatio} with OI ratio ${pcRatio.oiRatio}. Call volume: ${fmtK(pcRatio.callVol)}, put volume: ${fmtK(pcRatio.putVol)}. Elevated put flow can mean either protective hedging by long holders (bullish-neutral) or directional bearish positioning. The OI ratio distinguishes: if OI is also put-heavy, it's cumulative directional positioning, not just today's hedging. Cross-reference with GEX profile for dealer positioning context.`,
      sources:  ["Options Chain"],
      sector:   "EQUITY",
    };
  }
  if (vr < 0.6) {
    return {
      id:       "pc-call-heavy",
      strength: "MODERATE",
      type:     "OPTIONS FLOW",
      color:    C.green,
      icon:     "📈",
      title:    "CALL DOMINANCE",
      desc:     `Put/call ratio at ${pcRatio.volumeRatio} — call-heavy flow. OI ratio ${pcRatio.oiRatio}. Call volume: ${fmtK(pcRatio.callVol)}, put volume: ${fmtK(pcRatio.putVol)}. Could indicate bullish speculation or covered call selling pressure (bearish at the margin). If OI is also call-heavy, directional bet rather than income strategy. Check strike distribution — concentrated at one strike vs spread across the chain.`,
      sources:  ["Options Chain"],
      sector:   "EQUITY",
    };
  }
  return null;
}

function rulePetroleumInventory({ petroleum }) {
  if (!petroleum || petroleum.length < 2) return null;
  const latest = petroleum[petroleum.length - 1]?.value;
  const prior  = petroleum[petroleum.length - 2]?.value;
  if (!latest || !prior) return null;

  const draw = prior - latest; // positive = draw, negative = build
  if (Math.abs(draw) < 3) return null; // below noise threshold

  return {
    id:       "petroleum-move",
    strength: Math.abs(draw) > 8 ? "STRONG" : "MODERATE",
    type:     "ENERGY",
    color:    draw > 0 ? C.green : C.blue,
    icon:     "🛢",
    title:    draw > 0 ? "CRUDE INVENTORY DRAW" : "CRUDE INVENTORY BUILD",
    desc:     `EIA weekly ${draw > 0 ? "draw" : "build"} of ${fmt(Math.abs(draw), 1)}M barrels. Current inventory: ${fmt(latest, 1)}M bbl, prior week: ${fmt(prior, 1)}M bbl. ${draw > 0 ? "Draws signal demand strength or supply constraint — bullish crude." : "Builds signal demand softness or production increase — bearish crude."} Consecutive weeks in the same direction carry more signal weight than a single print. Next release: Wednesday 10:30 ET.`,
    sources:  ["EIA — Weekly Petroleum Status"],
    sector:   "ENERGY / COMMODITIES",
  };
}

function ruleTankerDark({ tankers }) {
  if (!Array.isArray(tankers)) return null;
  // ⚠ Placeholder rows never count, as in ruleCongressCluster. Every producer
  // already drops placeholder payloads; this is the second half of that guard.
  const dark = tankers.filter((v) =>
    v?.aisDark && String(v.source || "").toLowerCase() !== "representative");
  if (dark.length < 2) return null;

  return {
    id:       "tanker-dark-cluster",
    strength: dark.length >= 4 ? "STRONG" : "MODERATE",
    type:     "PHYSICAL WORLD",
    color:    C.orange,
    icon:     "🚢",
    title:    `${dark.length} VESSELS DARK`,
    desc:     `${dark.length} tankers with AIS transponders off: ${dark.map((v) => v.name).join(", ")}. Vessel transponder shutoff is a known tactic for sanction circumvention — used extensively by Iran, Russia, and Venezuela shadow fleets. A cluster of dark events near specific ports or in the same time window is a stronger signal than isolated incidents. Cross-reference against crude inventory data and freight rate movements.`,
    sources:  ["AIS Vessel Tracking"],
    sector:   "ENERGY / GEOPOLITICAL",
  };
}

function ruleThermalAnomaly({ fires }) {
  if (!fires) return null;
  const industrial = fires.filter((f) => f.type === "Industrial" && f.frp > 80);
  if (industrial.length < 2) return null;

  return {
    id:       "thermal-industrial",
    strength: "WEAK",
    type:     "PHYSICAL WORLD",
    color:    C.orange,
    icon:     "🔥",
    title:    "INDUSTRIAL THERMAL ANOMALIES",
    desc:     `${industrial.length} high-intensity industrial heat signatures detected (FRP >80MW): ${industrial.map((f) => f.label).join("; ")}. Elevated industrial thermal signatures near known refinery or production infrastructure can indicate operational disruption before official reports. FRP (Fire Radiative Power) above 80MW distinguishes industrial events from vegetation fires. Requires correlation with asset location data to confirm significance.`,
    sources:  ["NASA FIRMS — VIIRS Satellite"],
    sector:   "ENERGY / INDUSTRIAL",
  };
}

// A cluster is CONGRESS_CLUSTER_MIN_MEMBERS distinct members buying one ticker,
// filed within CONGRESS_CLUSTER_WINDOW_DAYS (UTC days, the last day counting), and
// more distinct buyers than sellers in that window. Rows are the mapped trades from
// /api/altdata/congress: type is BUY / SELL (EXCHANGE, "—" and anything else are
// ignored), filedDate is YYYY-MM-DD, politician is the member.
const CONGRESS_CLUSTER_MIN_MEMBERS = 3;
const CONGRESS_CLUSTER_WINDOW_DAYS = 30;
const CONGRESS_CLUSTER_MAX_LISTED = 5;
const DAY_MS = 86400000;
// Spellings of "no ticker" the feed can carry, as in the congress panel.
const CONGRESS_NO_TICKER = new Set(["--", "-", "—", "N/A"]);

// "YYYY-MM-DD" (first 10 chars) or "MM/DD/YYYY" → UTC midnight ms; else null.
// The round trip rejects impossible dates like 2026-13-45 or 02/30/2026.
function congressFiledDay(v) {
  const str = String(v ?? "").trim();
  let y, m, d, x;
  if ((x = /^(\d{4})-(\d{2})-(\d{2})/.exec(str))) [, y, m, d] = x;
  else if ((x = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(str))) [, m, d, y] = x;
  else return null;
  const ms = Date.UTC(+y, +m - 1, +d);
  const back = new Date(ms);
  return back.getUTCFullYear() === +y && back.getUTCMonth() === +m - 1 && back.getUTCDate() === +d
    ? ms : null;
}

function ruleCongressCluster({ congressTrades }) {
  if (!Array.isArray(congressTrades)) return null;
  const now = new Date();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const cutoff = today - CONGRESS_CLUSTER_WINDOW_DAYS * DAY_MS;

  const byTicker = new Map();   // ticker → { buy: Set(member), sell: Set(member) }
  for (const t of congressTrades) {
    // ⚠ Placeholder rows (the backend's REP_CONGRESS fallback; the Congressional
    // tab's own copy is gone) are flagged source: "representative". They never count.
    if (!t || String(t.source || "").toLowerCase() === "representative") continue;
    const side = String(t.type || "").trim().toUpperCase();
    if (side !== "BUY" && side !== "SELL") continue;
    const ticker = String(t.ticker || "").trim().toUpperCase();
    if (!ticker || CONGRESS_NO_TICKER.has(ticker)) continue;
    const member = String(t.politician || "").trim().replace(/\s+/g, " ").toLowerCase();
    if (!member || member === "—") continue;   // the backend's "no name" placeholder
    // Undated, unparseable and future filings are left out: the window is the point.
    const filed = congressFiledDay(t.filedDate);
    if (filed == null || filed < cutoff || filed > today) continue;
    if (!byTicker.has(ticker)) byTicker.set(ticker, { buy: new Set(), sell: new Set() });
    byTicker.get(ticker)[side === "BUY" ? "buy" : "sell"].add(member);
  }

  const qualifying = [...byTicker]
    .filter(([, g]) => g.buy.size >= CONGRESS_CLUSTER_MIN_MEMBERS && g.buy.size > g.sell.size)
    .map(([ticker, g]) => ({ ticker, members: g.buy.size }))
    .sort((a, b) => b.members - a.members || (a.ticker < b.ticker ? -1 : a.ticker > b.ticker ? 1 : 0));
  if (qualifying.length === 0) return null;

  const listed = qualifying.slice(0, CONGRESS_CLUSTER_MAX_LISTED);
  const tickers = listed.map((q) => `${q.ticker} (${q.members} members)`);
  const more = qualifying.length > listed.length ? ` (top ${listed.length} of ${qualifying.length})` : "";

  return {
    id:       "congress-buy-cluster",
    strength: "WEAK",
    type:     "REGULATORY SIGNAL",
    color:    C.purple,
    icon:     "🏛",
    title:    "CONGRESSIONAL PURCHASE CLUSTER",
    desc:     `${qualifying.length} ticker${qualifying.length === 1 ? "" : "s"} bought by ${CONGRESS_CLUSTER_MIN_MEMBERS}+ distinct members, filed in the last ${CONGRESS_CLUSTER_WINDOW_DAYS} days, with more buyers than sellers${more}: ${tickers.join(", ")}. STOCK Act filings lag by up to 45 days — the trades already happened. Cluster purchases in overlapping sectors warrant cross-referencing against committee assignments. Members with relevant committee oversight purchasing in that sector is a higher-signal pattern than random diversified buying.`,
    sources:  ["STOCK Act — House/Senate Disclosures"],
    sector:   "VARIES BY SECTOR",
  };
}

function ruleUnusualOptions({ unusualOptions }) {
  if (!unusualOptions || unusualOptions.length === 0) return null;
  // High vol/OI ratio with meaningful absolute volume
  const significant = unusualOptions.filter(
    (o) => o.ratio > 5 && o.volume > 500
  );
  if (significant.length === 0) return null;

  const top = significant[0];
  return {
    id:       "unusual-options-flow",
    strength: top.ratio > 20 ? "STRONG" : "MODERATE",
    type:     "OPTIONS FLOW",
    color:    C.amber,
    icon:     "⚡",
    title:    "UNUSUAL OPTIONS ACTIVITY",
    desc:     `${significant.length} strike(s) with Vol/OI ratio above 5x and volume >500 contracts. Top: $${top.strike} ${top.isCall ? "CALL" : "PUT"} — ${fmtK(top.volume)} contracts vs ${fmtK(top.oi)} OI (${fmt(top.ratio, 1)}x ratio). High Vol/OI on a strike means today's volume far exceeds existing positioning — typically signals new directional conviction rather than position rolling. IV at these strikes relative to the ATM IV surface indicates whether the buyer is paying up for the move.`,
    sources:  ["Options Chain — Unusual Volume Screen"],
    sector:   "EQUITY",
  };
}

function ruleFederalContracts({ contracts }) {
  if (!contracts || contracts.length === 0) return null;
  // Flag if any single award is > $1B — material for smaller companies
  const large = contracts.filter((c) => c.amount > 1e9);
  if (large.length === 0) return null;

  return {
    id:       "large-contract-award",
    strength: "WEAK",
    type:     "GOVERNMENT FLOW",
    color:    C.amber,
    icon:     "📋",
    title:    `${large.length} LARGE CONTRACT AWARD${large.length > 1 ? "S" : ""}`,
    desc:     `${large.length} federal contract award${large.length > 1 ? "s" : ""} exceeding $1B detected: ${large.map((c) => `${c.company} ($${(c.amount / 1e9).toFixed(1)}B from ${c.agency})`).join("; ")}. Government contract awards are public record and appear on USASpending.gov within days of award — typically before any company press release or analyst update. For companies where federal revenue is >10% of total, awards of this scale are material.`,
    sources:  ["USASpending.gov"],
    sector:   "DEFENSE / GOVERNMENT SERVICES",
  };
}

function ruleNewsSentiment({ newsSentiment }) {
  if (!newsSentiment) return null;
  const { score, negativeCount, positiveCount, neutralCount } = newsSentiment;
  const total = negativeCount + positiveCount + neutralCount;
  if (total < 5) return null; // not enough articles

  const negativePct = negativeCount / total;
  const positivePct = positiveCount / total;

  if (negativePct >= 0.70) {
    return {
      id:       "news-negative",
      strength: negativePct >= 0.85 ? "MODERATE" : "WEAK",
      type:     "NEWS SENTIMENT",
      color:    "#FF5252",
      icon:     "📰",
      title:    "NEGATIVE NEWS SENTIMENT DOMINANT",
      desc:     `${Math.round(negativePct * 100)}% of current financial ` +
                `news coverage is negative sentiment (${negativeCount} of ` +
                `${total} articles). Historically correlates with elevated ` +
                `fear. Contrarian signal when combined with extreme Fear & ` +
                `Greed readings — widespread negative coverage can mark ` +
                `short-term sentiment exhaustion.`,
      sources:  ["NewsData.io", "GNews", "Currents API"],
      sector:   "MACRO / SENTIMENT",
    };
  }
  if (positivePct >= 0.75) {
    return {
      id:       "news-positive",
      strength: "WEAK",
      type:     "NEWS SENTIMENT",
      color:    C.green,
      icon:     "📰",
      title:    "POSITIVE NEWS SENTIMENT DOMINANT",
      desc:     `${Math.round(positivePct * 100)}% of current financial ` +
                `news coverage is positive sentiment. Widespread positive ` +
                `coverage at market peaks has historically preceded ` +
                `corrections — contrarian read when combined with elevated ` +
                `CNN Fear & Greed or a crowded QS Bull/Bear reading.`,
      sources:  ["NewsData.io", "GNews", "Currents API"],
      sector:   "MACRO / SENTIMENT",
    };
  }
  return null;
}

function ruleMVGap({ monetaryRegime }) {
  if (!monetaryRegime) return null;
  const { mvGap, lendingGrowth, velocityTrend } = monetaryRegime;
  if (mvGap == null || lendingGrowth == null) return null;
  // Friedman configuration: nominal demand outrunning output, credit expanding,
  // and velocity recovering — all three required.
  if (mvGap > 5 && lendingGrowth > 8 && velocityTrend === "recovering") {
    return {
      id:       "mv-gap-inflationary",
      strength: mvGap > 8 ? "STRONG" : "MODERATE",
      type:     "MONETARY REGIME",
      color:    C.red,
      icon:     "💵",
      title:    "INFLATIONARY MONETARY CONFIGURATION",
      desc:     `Friedman test (MV=PQ): nominal demand — M2 money supply × velocity — is growing ${fmt(monetaryRegime.mvGrowth, 1)}% annualized while real output (GDP) grows ${fmt(monetaryRegime.gdpGrowth, 1)}%, a ${fmt(mvGap, 1)}pp gap. Bank lending is expanding ${fmt(lendingGrowth, 1)}% annualized and M2 velocity is recovering (${fmt(monetaryRegime.velocityPrior, 3)} → ${fmt(monetaryRegime.velocityLatest, 3)}). When money × velocity persistently outruns real output, the excess resolves into prices. This is a regime signal on a 6–24 month horizon — not a trade signal.`,
      sources:  ["FRED — M2SL", "FRED — M2V", "FRED — TOTLL", "FRED — GDPC1"],
      sector:   "MACRO / MONETARY",
    };
  }
  return null;
}

function ruleFiscalDominance({ monetaryRegime }) {
  if (!monetaryRegime) return null;
  const { fiscalDominance } = monetaryRegime;
  if (fiscalDominance == null) return null;
  // Treasury bills above 20% of total debt → rollover needs constrain Fed policy.
  if (fiscalDominance > 20) {
    return {
      id:       "fiscal-dominance",
      strength: "MODERATE",
      type:     "MONETARY REGIME",
      color:    C.amber,
      icon:     "🏛",
      title:    "FISCAL DOMINANCE — FED INDEPENDENCE CONSTRAINED",
      desc:     `Treasury bills are ${fmt(fiscalDominance, 1)}% of total federal debt outstanding — above the 20% threshold where continuous refinancing needs constrain the Fed's rate policy (fiscal dominance). Short-dated debt must be rolled over constantly; sustained high short rates balloon interest costs and pressure the Fed toward accommodation. Shorter horizon than the MV gap — 3–6 months.`,
      sources:  ["FRED — MKTGDP", "FRED — federal debt metrics"],
      sector:   "MACRO / MONETARY",
    };
  }
  return null;
}

function ruleSentimentExtreme({ sentimentExtreme }) {
  if (!sentimentExtreme) return null;
  const { composite, sources_aligned } = sentimentExtreme;
  if (composite == null) return null;
  // Fire only at an extreme composite (<20 or >80) with broad agreement across
  // the underlying sources — a single source at an extreme is noise; 3+ aligned
  // is the convergence the old SentimentBoard flagged as contrarian.
  // ⚠ NO PRODUCER. SentimentBoard.jsx was the only component that pushed
  // sentimentExtreme, and it was never mounted (deleted 2026-10-27), so this rule
  // cannot fire. Kept until a new producer exists or the rule is removed.
  if (!(composite < 20 || composite > 80)) return null;
  if (!(sources_aligned >= 3)) return null;

  const isFear = composite < 20;
  const strength = (composite < 15 || composite > 85) ? "STRONG" : "ELEVATED";
  return {
    id:       "sentiment-extreme",
    strength,
    type:     "sentiment-extreme",
    color:    isFear ? C.green : C.red,   // fear → contrarian bullish, greed → contrarian bearish
    icon:     "🎭",
    title:    "SENTIMENT EXTREME",
    desc:     isFear
      ? `Composite sentiment at extreme fear (${composite}/100). ${sources_aligned} sources aligned. Historically contrarian bullish signal.`
      : `Composite sentiment at extreme greed (${composite}/100). ${sources_aligned} sources aligned. Historically contrarian bearish signal.`,
    sources:  ["Sentiment Board Composite"],
    sector:   "MACRO / SENTIMENT",
  };
}

// Dark pool institutional activity + elevated short interest on the same names —
// a squeeze setup with institutional confirmation. Fed by darkPoolSqueeze, the
// frontend cross-reference of the dark-pool scan against squeeze candidates.
function ruleDarkPoolSqueeze({ darkPoolSqueeze }) {
  if (!Array.isArray(darkPoolSqueeze) || darkPoolSqueeze.length === 0) return null;
  const syms = darkPoolSqueeze.map((h) => h && h.symbol).filter(Boolean);
  if (!syms.length) return null;
  const n = syms.length;
  return {
    id:       "dark-pool-squeeze",
    strength: n >= 2 ? "STRONG" : "ELEVATED",
    type:     "dark-pool-squeeze",
    color:    "#9D4EDD",
    icon:     "⚡",
    title:    "DARK POOL + SQUEEZE CONVERGENCE",
    desc:     `${n} symbol${n === 1 ? "" : "s"} showing institutional dark pool activity and elevated short interest: ${syms.join(", ")}. Off-exchange accumulation paired with a crowded short base is a classic squeeze-ignition setup — institutional positioning may be front-running a covering event.`,
    sources:  ["Dark Pool Scanner", "FINRA Short Interest"],
    sector:   "EQUITY / MICROSTRUCTURE",
  };
}

// ─── RULES REGISTRY ───────────────────────────────────────────────────────────
const RULES = [
  ruleVixSkewDivergence,
  ruleVixFear,
  ruleVixComplacency,
  ruleYieldCurve,
  rulePutCallRatio,
  rulePetroleumInventory,
  ruleTankerDark,
  ruleThermalAnomaly,
  ruleCongressCluster,
  ruleUnusualOptions,
  ruleFederalContracts,
  ruleNewsSentiment,
  ruleMVGap,
  ruleFiscalDominance,
  ruleSentimentExtreme,
  ruleDarkPoolSqueeze,
];

// ─── NULL STATE ────────────────────────────────────────────────────────────────
const NO_SIGNALS = {
  id:       "none",
  strength: "NONE",
  type:     "STATUS",
  color:    C.textDim,
  icon:     "◯",
  title:    "NO ACTIVE CONFLUENCES",
  desc:     "No signal thresholds breached across monitored data sources. Load data on individual tabs to populate detection — the board updates automatically as each panel fetches live data. Signal detection runs on every data update.",
  sources:  [],
  sector:   "—",
};

// ─── FACTOR FRAMEWORK (Phase 1B) ──────────────────────────────────────────────
// Display-only factor context wired onto the Signal Board. Detection logic and
// signal ids are UNCHANGED — these maps translate this board's signal ids to the
// canonical factor types and hold hardcoded academic factor loadings (from
// established research, not computed at runtime in this phase).

// Canonical signal type → dominant factor label (used for breadth grouping).
const SIGNAL_FACTOR_LABEL = {
  "vix-skew-divergence":   "VOLATILITY REGIME",
  "yield-curve-inversion": "MACRO RISK-OFF",
  "monetary-regime":       "MACRO RISK-OFF",
  "heston-surface":        "VOLATILITY REGIME",
  "squeeze":               "IDIOSYNCRATIC",
  "dark-pool-defended":    "INSTITUTIONAL FLOW",
  "unusual-options":       "INSTITUTIONAL FLOW",
  "congress-trade":        "GOVERNMENT FLOW",
  "ais-dark":              "PHYSICAL WORLD",
  "petroleum-draw":        "PHYSICAL WORLD",
  "sentiment-extreme":     "SENTIMENT",
  "fiscal-dominance":      "MACRO RISK-OFF",
};

// Bridge: this board's actual signal ids → canonical factor type above.
// (Signal detection and ids are not modified; this is a display-only lookup.)
const SIGNAL_ID_TO_TYPE = {
  "vix-skew-div":         "vix-skew-divergence",
  "yield-inverted":       "yield-curve-inversion",
  "yield-flat":           "yield-curve-inversion",
  "mv-gap-inflationary":  "monetary-regime",
  "fiscal-dominance":     "fiscal-dominance",
  "unusual-options-flow": "unusual-options",
  "congress-buy-cluster": "congress-trade",
  "tanker-dark-cluster":  "ais-dark",
  "petroleum-move":       "petroleum-draw",
  "news-negative":        "sentiment-extreme",
  "news-positive":        "sentiment-extreme",
  "sentiment-extreme":    "sentiment-extreme",
};

// Signals with no dedicated factor-context entry still participate in breadth
// grouping via a fallback label, so correlated vol / flow / physical signals
// collapse into one independent position rather than being over-counted.
const FALLBACK_FACTOR_LABEL = {
  "vix-fear":             "VOLATILITY REGIME",
  "vix-complacent":       "VOLATILITY REGIME",
  "pc-put-heavy":         "INSTITUTIONAL FLOW",
  "pc-call-heavy":        "INSTITUTIONAL FLOW",
  "thermal-industrial":   "PHYSICAL WORLD",
  "large-contract-award": "GOVERNMENT FLOW",
};

// Hardcoded factor loadings + regime context, keyed by canonical signal type.
const FACTOR_CONTEXT = {
  "vix-skew-divergence": {
    loadings: [["Volatility", 0.8], ["Quality", 0.4], ["Momentum", -0.6]],
    regime: "Volatility spike regime",
    historical: "Low vol and quality outperform. Momentum underperforms.",
    horizon: "1-6 months",
  },
  "yield-curve-inversion": {
    loadings: [["Quality", 0.7], ["Value", 0.5], ["Momentum", -0.4]],
    regime: "Late cycle / recession risk",
    historical: "Quality and value outperform. High beta and momentum underperform.",
    horizon: "6-18 months",
  },
  "monetary-regime": {
    loadings: [["Value", 0.6], ["Size", -0.3], ["Momentum", 0.4]],
    regime: "Inflationary monetary configuration",
    historical: "Hard assets and value outperform. Long duration underperforms.",
    horizon: "6-24 months",
  },
  "squeeze": {
    loadings: [["Idiosyncratic", 0.9], ["Size", -0.5]],
    regime: "Short squeeze / idiosyncratic event",
    historical: "Stock-specific — not factor-driven. Factor models have low explanatory power here.",
    horizon: "Days to weeks",
  },
  "dark-pool-defended": {
    loadings: [["Institutional Flow", 0.8], ["Momentum", 0.3]],
    regime: "Institutional accumulation signal",
    historical: "Momentum continuation more likely. Reversal risk if level breaks.",
    horizon: "1-4 weeks",
  },
  "unusual-options": {
    loadings: [["Institutional Flow", 0.7], ["Volatility", 0.5]],
    regime: "Informed positioning signal",
    historical: "Direction depends on put vs call bias. IV expansion typical in short term.",
    horizon: "Days to weeks",
  },
  "ais-dark": {
    loadings: [["Physical World", 0.8], ["Value", 0.3]],
    regime: "Supply disruption signal",
    historical: "Energy and commodity names outperform. Consumer discretionary underperforms.",
    horizon: "2-8 weeks",
  },
  "petroleum-draw": {
    loadings: [["Physical World", 0.7], ["Value", 0.4]],
    regime: "Energy supply tightening",
    historical: "Energy sector outperforms on sustained draws.",
    horizon: "2-6 weeks",
  },
  "congress-trade": {
    loadings: [["Government Flow", 0.8], ["Sector-specific", 0.6]],
    regime: "Informed government insider signal",
    historical: "Sector concentration matters more than market.",
    horizon: "1-6 months",
  },
  "sentiment-extreme": {
    loadings: [["Sentiment", 0.9], ["Momentum", -0.5]],
    regime: "Contrarian signal at sentiment extremes",
    historical: "Contrarian — market tends to reverse. Most powerful at multi-year sentiment extremes.",
    horizon: "1-3 months",
  },
  "fiscal-dominance": {
    loadings: [["Macro Risk-Off", 0.7], ["Value", 0.4], ["Quality", 0.3]],
    regime: "Fed independence constrained",
    historical: "Hard assets outperform. Long duration bonds underperform. Dollar weakness.",
    horizon: "6-18 months",
  },
};

// Loading names that correspond to computed portfolio factor keys (Phase 2).
const FACTOR_KEY_MAP = {
  Volatility: "volatility",
  Quality:    "quality",
  Momentum:   "momentum",
  Value:      "value",
  Size:       "size",
  Beta:       "beta",
};

// Shared style for the small factor-context section labels.
const FACTOR_SECTION_LABEL = {
  fontSize: 9,
  color: C.textDim,
  fontWeight: 700,
  letterSpacing: "0.1em",
  marginTop: 8,
  marginBottom: 3,
};

function factorTypeOf(sig) {
  return SIGNAL_ID_TO_TYPE[sig.id] || null;
}

function factorContextOf(sig) {
  const type = factorTypeOf(sig);
  return type ? FACTOR_CONTEXT[type] : null;
}

function factorLabelOf(sig) {
  const type = factorTypeOf(sig);
  if (type && SIGNAL_FACTOR_LABEL[type]) return SIGNAL_FACTOR_LABEL[type];
  return FALLBACK_FACTOR_LABEL[sig.id] || null;
}

// Group active signals by shared factor label. Signals sharing a label are one
// independent position; unlabeled signals each count as their own position.
function computeBreadth(activeSignals) {
  const groups = new Map(); // label → [sig]
  let orphan = 0;
  for (const sig of activeSignals) {
    const label = factorLabelOf(sig);
    const key = label || `__orphan_${orphan++}`;
    if (!groups.has(key)) groups.set(key, { label: label || sig.type || "UNCLASSIFIED", signals: [] });
    groups.get(key).signals.push(sig);
  }
  const groupList = [...groups.values()];
  return {
    active: activeSignals.length,
    independent: groupList.length,
    groups: groupList,
  };
}

// Portfolio implication — only renders once Phase 2 pushes computed watchlist
// factor exposures onto globalSignals.portfolioExposures. Absent until then.
function portfolioImplication(ctx, globalSignals) {
  const exposures = globalSignals?.portfolioExposures;
  if (!exposures || !ctx?.loadings?.length) return null;
  const [name] = ctx.loadings[0]; // dominant loading
  const key = FACTOR_KEY_MAP[name];
  if (!key) return null;
  const val = exposures[key];
  if (val == null) return null;
  const mag = Math.abs(val);
  const level = mag >= 0.66 ? "HIGH" : mag >= 0.33 ? "MODERATE" : "LOW";
  return { factor: name, value: val, level };
}

// ─── PERSONAL RELEVANCE (factor sensitivity → portfolio) ──────────────────────
// Display-only scoring: how strongly a firing signal's factor loadings overlap
// with the user's own portfolio factor exposures. Purely additive — reads
// SIGNAL_FACTOR_MAP + SIGNAL_ID_TO_TYPE + globalSignals.portfolioExposures and
// never touches detection, rules, or the factor-context section. Null-safe: a
// missing mapping or missing exposure degrades to the monitor floor.

// Score → band label (thresholds per spec; note score 20 lands in LOW).
function relevanceBand(score) {
  if (score >= 80) return "CRITICAL";
  if (score >= 60) return "HIGH";
  if (score >= 40) return "MODERATE";
  if (score >= 20) return "LOW";
  return "MONITOR";
}

// 0-100 overlap score. Averages the user's exposure magnitude across the factors
// this signal loads on — each mapped factor weighted equally (SIGNAL_FACTOR_MAP
// encodes WHICH factors apply, not per-factor magnitudes). No mapping / no usable
// exposure → 20.
function computeRelevanceScore(signal, exposures) {
  const type = signal && SIGNAL_ID_TO_TYPE[signal.id];
  const mapping = type ? SIGNAL_FACTOR_MAP[type] : null;
  if (!mapping || !mapping.factors || !mapping.factors.length) return 20;
  if (!exposures) return 20;
  const vals = [];
  for (const f of mapping.factors) {
    const v = exposures[f];
    if (v == null) continue;
    vals.push(Math.min(1, Math.abs(v))); // exposure treated on a 0-1 magnitude scale
  }
  if (!vals.length) return 20;
  const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
  return Math.round(Math.max(0, Math.min(100, avg * 100)));
}

// Short object-noun describing the signal's regime, keyed by canonical type.
const RELEVANCE_TYPE_PHRASE = {
  "vix-skew-divergence":   "volatility-regime moves",
  "vix-fear":              "volatility-regime moves",
  "yield-curve-inversion": "rate-environment shifts",
  "monetary-regime":       "monetary-regime shifts",
  "fiscal-dominance":      "fiscal-regime shifts",
  "dark-pool-defended":    "institutional-flow moves",
  "squeeze":               "squeeze dynamics",
  "pc-put-heavy":          "options-flow moves",
  "ais-dark":              "supply-disruption moves",
  "tanker-dark-cluster":   "supply-disruption moves",
  "petroleum-draw":        "energy-supply moves",
  "thermal-industrial":    "industrial-disruption moves",
  "congress-trade":        "insider-flow patterns",
  "sentiment-extreme":     "sentiment reversals",
};

// One plain-language sentence (<15 words) describing the factor relationship
// only — no recommendation, no advice. Built from the signal's canonical type,
// the user's highest-exposed mapped factor, and that factor's magnitude.
function getRelevanceImplication(signal, exposures, score) {
  const type = signal && SIGNAL_ID_TO_TYPE[signal.id];
  const mapping = type ? SIGNAL_FACTOR_MAP[type] : null;
  if (!mapping || !mapping.factors || !mapping.factors.length || !exposures) {
    return "Limited factor overlap with current portfolio profile.";
  }
  let topFactor = null;
  let topMag = -1;
  for (const f of mapping.factors) {
    const v = exposures[f];
    if (v == null) continue;
    const mag = Math.abs(v);
    if (mag > topMag) { topMag = mag; topFactor = f; }
  }
  if (!topFactor || topMag <= 0) {
    return "Limited factor overlap with current portfolio profile.";
  }
  const phrase = RELEVANCE_TYPE_PHRASE[type] || "this signal";
  const level = topMag >= 0.66 ? "High" : topMag >= 0.33 ? "Moderate" : "Low";
  const verb  = topMag >= 0.66 ? "amplifies" : topMag >= 0.33 ? "aligns with" : "weakly aligns with";
  const factorName = topFactor.charAt(0).toUpperCase() + topFactor.slice(1);
  return `${level} ${factorName} exposure ${verb} ${phrase}.`;
}

// Band → badge palette. Existing C tokens + alpha only — no new color variables.
// dim=true renders the MODERATE tier as a muted amber; LOW/MONITOR use slate.
const RELEVANCE_BAND_STYLE = {
  CRITICAL: { base: C.red,     dim: false },
  HIGH:     { base: C.orange,  dim: false },
  MODERATE: { base: C.orange,  dim: true  },
  LOW:      { base: C.textMid, dim: false },
  MONITOR:  { base: C.textDim, dim: false },
};

// ─── FACTOR DECAY (Phase 1C) ──────────────────────────────────────────────────
// Research-based typical regime durations (calendar days) per canonical signal
// type. Resolved from a signal id via the SIGNAL_ID_TO_TYPE bridge above.
const SIGNAL_DURATION = {
  "vix-skew-divergence":   { min: 5,  max: 30,  avg: 18 },
  "yield-curve-inversion": { min: 90, max: 540, avg: 280 },
  "monetary-regime":       { min: 90, max: 540, avg: 240 },
  "fiscal-dominance":      { min: 60, max: 360, avg: 180 },
  "squeeze":               { min: 1,  max: 14,  avg: 5 },
  "dark-pool-defended":    { min: 3,  max: 21,  avg: 10 },
  "unusual-options":       { min: 1,  max: 7,   avg: 3 },
  "ais-dark":              { min: 3,  max: 30,  avg: 14 },
  "petroleum-draw":        { min: 7,  max: 42,  avg: 21 },
  "congress-trade":        { min: 30, max: 180, avg: 90 },
  "sentiment-extreme":     { min: 14, max: 90,  avg: 45 },
};
const DEFAULT_DURATION = { min: 7, max: 60, avg: 30 };

const DECAY_COLOR = {
  LOW:      C.green,
  MODERATE: C.amber,
  HIGH:     C.red,
  ELEVATED: C.red,
};

// Decay probability from how long a signal has been continuously active vs its
// typical duration. Returns null when detectedAt is missing (show nothing).
function decayFor(sig, detectedAt) {
  if (detectedAt == null) return null;
  const type = SIGNAL_ID_TO_TYPE[sig.id];
  const d = (type && SIGNAL_DURATION[type]) || DEFAULT_DURATION;
  const daysActive = (Date.now() - detectedAt) / 86400000;
  let level;
  if (daysActive < d.min) level = "LOW";
  else if (daysActive < d.avg) level = "MODERATE";
  else if (daysActive < d.max) level = "HIGH";
  else level = "ELEVATED";
  return { days: Math.max(0, Math.floor(daysActive)), min: d.min, max: d.max, level };
}

// ─── SIGNAL FIRING HISTORY (Phase 3.5) ────────────────────────────────────────
// Persistent log of when each signal type fires and resolves. Exposed to
// FactorBacktest via globalSignals.signalHistory so it can compute real
// historical factor performance. Accumulates across sessions; never cleared on
// logout — signal history is valuable data.
const SIGNAL_HISTORY_KEY = "qs_signal_history";
const MAX_SIGNAL_HISTORY = 500;

// Canonical type used as the history key — matches FactorBacktest's typeOf().
function signalTypeOf(id) {
  return SIGNAL_ID_TO_TYPE[id] || id;
}

function loadSignalHistory() {
  try {
    const parsed = JSON.parse(localStorage.getItem(SIGNAL_HISTORY_KEY));
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveSignalHistory(history) {
  const trimmed =
    history.length > MAX_SIGNAL_HISTORY ? history.slice(-MAX_SIGNAL_HISTORY) : history;
  try {
    localStorage.setItem(SIGNAL_HISTORY_KEY, JSON.stringify(trimmed));
  } catch {
    /* quota — history is best-effort */
  }
  return trimmed;
}

function getOpenEntry(history, signalType) {
  let found = null;
  for (const e of history) {
    // Entries are appended chronologically, so the last match is most recent.
    if (e && e.signalType === signalType && e.resolvedAt == null) found = e;
  }
  return found;
}

// ─── BREADTH BAR ──────────────────────────────────────────────────────────────
function BreadthBar({ activeSignals }) {
  const [open, setOpen] = useState(false);
  const { active, independent, groups } = computeBreadth(activeSignals);
  const allIndependent = independent === active;

  return (
    <div style={{
      background: C.panel,
      borderBottom: `1px solid ${C.border}`,
      padding: "8px 12px",
      marginBottom: 10,
    }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
      }}>
        <span style={{ fontSize: 10, color: C.textDim }}>
          ACTIVE SIGNALS: {active}
        </span>
        <span style={{ fontSize: 10, fontWeight: 700, color: allIndependent ? C.green : C.amber }}>
          INDEPENDENT POSITIONS: {independent}
        </span>
        <span style={{ fontSize: 9, color: C.textDim }}>
          IR ∝ skill × √{independent} · ×{fmt(Math.sqrt(independent), 2)}
        </span>
      </div>

      {/* Collapsible groupings — collapsed by default */}
      <div
        onClick={() => setOpen((o) => !o)}
        style={{ marginTop: 5, fontSize: 9, color: C.textDim, cursor: "pointer", userSelect: "none" }}
      >
        {open ? "▴" : "▾"} groupings
      </div>
      {open && (
        <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 3 }}>
          {groups.map((g, i) => (
            <div key={`${g.label}-${i}`} style={{ fontSize: 9, color: C.textMid, lineHeight: 1.6 }}>
              <span style={{ color: C.textDim, fontWeight: 700, letterSpacing: "0.08em" }}>{g.label}</span>
              {" ("}
              {g.signals.map((sg) => sg.title).join(", ")}
              {") — 1 position"}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── SIGNAL CARD ──────────────────────────────────────────────────────────────
function SignalCard({ sig, anthropicKey, globalSignals, detectedAt }) {
  const strengthCol = STRENGTH_COLOR[sig.strength] || C.textDim;
  const [factorOpen, setFactorOpen] = useState(false);
  const ctx = sig.id !== "none" ? factorContextOf(sig) : null;
  const portImpl = ctx ? portfolioImplication(ctx, globalSignals) : null;
  const decay = sig.id !== "none" ? decayFor(sig, detectedAt) : null;

  // Personal relevance — only when the portfolio's factor exposures are present.
  const relExposures = globalSignals?.portfolioExposures;
  const relScore = sig.id !== "none" && relExposures ? computeRelevanceScore(sig, relExposures) : null;
  const relBand = relScore != null ? relevanceBand(relScore) : null;
  const relSentence = relScore != null ? getRelevanceImplication(sig, relExposures, relScore) : null;
  const relStyle = relBand ? (RELEVANCE_BAND_STYLE[relBand] || RELEVANCE_BAND_STYLE.MONITOR) : null;
  const relText = relStyle ? relStyle.base + (relStyle.dim ? "AA" : "") : null;
  const relBg = relStyle ? relStyle.base + (relStyle.dim ? "10" : "18") : null;
  const relBorder = relStyle ? relStyle.base + (relStyle.dim ? "24" : "33") : null;

  return (
    <div style={{
      ...s.card(sig.color),
      borderLeft: `3px solid ${sig.color}`,
    }}>
      {/* Card header */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: 7,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <span style={{ fontSize: 15 }}>{sig.icon}</span>
          <div>
            <div style={{
              fontSize: 15,
              fontWeight: 700,
              color: sig.color,
              letterSpacing: "0.08em",
              marginBottom: 4,
            }}>
              {sig.title}
            </div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              <span style={s.tag(sig.color)}>{sig.type}</span>
              <span style={s.tag()}>{sig.sector}</span>
              {sig.sources.map((src) => (
                <span key={src} style={s.tag()}>{src}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Strength badge */}
        <span style={{
          fontSize: 13,
          fontWeight: 700,
          color: strengthCol,
          padding: "2px 7px",
          background: strengthCol + "18",
          borderRadius: 2,
          flexShrink: 0,
          border: `1px solid ${strengthCol}33`,
          marginLeft: 8,
        }}>
          {sig.strength}
        </span>
      </div>

      {/* Factor decay indicator — always visible, subtle (Phase 1C) */}
      {decay && (
        <div style={{
          fontSize: 9,
          color: DECAY_COLOR[decay.level] || C.textDim,
          marginBottom: 7,
          letterSpacing: "0.04em",
          ...(decay.level === "ELEVATED" ? { animation: "qsPulse 2s ease-in-out infinite" } : {}),
        }}>
          ACTIVE {decay.days} DAY{decay.days === 1 ? "" : "S"} · AVG DURATION {decay.min}-{decay.max} DAYS · DECAY PROBABILITY: {decay.level}
        </div>
      )}

      {/* Description */}
      <div style={{
        fontSize: 14,
        color: C.textMid,
        lineHeight: 1.75,
        marginBottom: sig.id !== "none" && (anthropicKey || window.QUANT_SIGNAL_BACKEND) ? 10 : 0,
      }}>
        {sig.desc}
      </div>

      {/* Analyst button — only on real signals, only if key present */}
      {sig.id !== "none" && (anthropicKey || window.QUANT_SIGNAL_BACKEND) && (
        <AnalystButton
          anthropicKey={anthropicKey}
          label="SYNTHESIZE"
          context={
            `Signal detected: ${sig.title}\n` +
            `Strength: ${sig.strength}\n` +
            `Type: ${sig.type}\n` +
            `Sector: ${sig.sector}\n` +
            `Sources: ${sig.sources.join(", ")}\n\n` +
            `Signal description: ${sig.desc}\n\n` +
            `Provide thorough analysis covering:\n` +
            `1. The historical precedents for this signal type and their outcomes\n` +
            `2. The mechanical reason this signal works (not just correlation)\n` +
            `3. What other signals would confirm or contradict this reading\n` +
            `4. The plausible scenario range — bull, base, bear cases for what this precedes\n` +
            `5. Time horizon considerations — is this a 1-week, 1-month, or 6-month signal\n` +
            `Be specific and direct. Use data from the signal description where relevant.`
          }
        />
      )}

      {/* Factor context — new collapsible section, below existing card content */}
      {ctx && (
        <div style={{ marginTop: 10, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
          <div
            onClick={() => setFactorOpen((o) => !o)}
            style={{
              fontSize: 9,
              color: C.textDim,
              cursor: "pointer",
              userSelect: "none",
              fontWeight: 700,
              letterSpacing: "0.1em",
            }}
          >
            FACTOR CONTEXT {factorOpen ? "▴" : "▾"}
          </div>

          {factorOpen && (
            <div style={{ marginTop: 6 }}>
              <div style={FACTOR_SECTION_LABEL}>FACTOR LOADINGS</div>
              {ctx.loadings.map(([name, val]) => (
                <div key={name} style={{ fontSize: 10, lineHeight: 1.6 }}>
                  <span style={{ color: C.textMid }}>{name}: </span>
                  <span style={{ color: val >= 0 ? C.green : C.red, fontWeight: 700 }}>
                    {val >= 0 ? "+" : ""}{val.toFixed(1)}
                  </span>
                </div>
              ))}

              <div style={FACTOR_SECTION_LABEL}>REGIME</div>
              <div style={{ fontSize: 10, color: C.textDim, lineHeight: 1.6 }}>{ctx.regime}</div>

              <div style={FACTOR_SECTION_LABEL}>HISTORICAL PATTERN</div>
              <div style={{ fontSize: 10, color: C.textDim, lineHeight: 1.6 }}>{ctx.historical}</div>

              <div style={{ fontSize: 10, color: C.amber, fontWeight: 700, lineHeight: 1.6, marginTop: 8 }}>
                SIGNAL HORIZON: {ctx.horizon}
              </div>

              {portImpl && (
                <>
                  <div style={FACTOR_SECTION_LABEL}>PORTFOLIO IMPLICATION</div>
                  <div style={{ fontSize: 10, color: C.textMid, lineHeight: 1.6 }}>
                    Your {portImpl.factor} exposure: {fmt(portImpl.value, 2)} ← {portImpl.level}
                  </div>
                  <div style={{ fontSize: 10, color: C.textMid, lineHeight: 1.6 }}>
                    Net exposure to this signal: {portImpl.level} RISK
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Personal relevance — factor overlap with the user's portfolio.
          Renders only when portfolioExposures is present (null-guarded above).
          Additive: sits below all existing card content. */}
      {relBand && (
        <div style={{ marginTop: 10, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
          <div style={FACTOR_SECTION_LABEL}>PERSONAL RELEVANCE</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{
              fontSize: 11,
              fontWeight: 700,
              color: relText,
              letterSpacing: "0.08em",
              padding: "2px 7px",
              background: relBg,
              border: `1px solid ${relBorder}`,
              borderRadius: 2,
            }}>
              {relBand}
            </span>
            <span style={{ fontSize: 10, color: C.textDim }}>{relScore}/100</span>
          </div>
          <div style={{ fontSize: 10, color: C.textMid, lineHeight: 1.6, marginTop: 5 }}>
            {relSentence}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SIGNAL BOARD ─────────────────────────────────────────────────────────────
export function SignalBoard({ keys, globalSignals, onData }) {
  const [signals,  setSignals]  = useState([NO_SIGNALS]);
  const [lastScan, setLastScan] = useState(null);
  const [scanCount, setScanCount] = useState(0);
  // First-seen timestamps per active signal id (detectedAt), persisted so a
  // signal's "days active" survives reloads/tab switches. Cleared when a signal
  // deactivates so re-activation restarts its clock. This does not alter signal
  // detection — it only stamps when each detected signal first appeared.
  const [firstSeen, setFirstSeen] = useState(() => {
    try { return JSON.parse(localStorage.getItem("qs_signal_first_seen")) || {}; }
    catch { return {}; }
  });
  // Card-list sort mode — "STRENGTH" (default, existing rule order) or
  // "RELEVANCE". Persisted so the choice survives reloads/tab switches.
  const [sortMode, setSortMode] = useState(() => {
    try { return localStorage.getItem("qs_signal_sort") === "RELEVANCE" ? "RELEVANCE" : "STRENGTH"; }
    catch { return "STRENGTH"; }
  });
  const toggleSort = () => {
    setSortMode((m) => {
      const next = m === "RELEVANCE" ? "STRENGTH" : "RELEVANCE";
      try { localStorage.setItem("qs_signal_sort", next); } catch { /* quota */ }
      return next;
    });
  };
  const pushedRef = useRef("");
  // Previous cycle's active signal types + last-published history, for diffing
  // activations/deactivations and avoiding a globalSignals update loop.
  const prevTypesRef = useRef(new Set());
  const historyPushedRef = useRef("");

  useEffect(() => {
    if (!globalSignals || Object.keys(globalSignals).length === 0) return;

    // Run all rules against current globalSignals
    const detected = RULES
      .map((rule) => {
        try { return rule(globalSignals); }
        catch { return null; }
      })
      .filter(Boolean);

    setSignals(detected.length > 0 ? detected : [NO_SIGNALS]);
    setLastScan(new Date().toLocaleTimeString());
    setScanCount((n) => n + 1);

    const active = detected.filter((sig) => sig.id !== "none");

    // Stamp first-seen for newly active signals; drop signals no longer active.
    setFirstSeen((prev) => {
      const now = Date.now();
      const next = {};
      for (const sig of active) next[sig.id] = prev[sig.id] || now;
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      const unchanged =
        prevKeys.length === nextKeys.length && nextKeys.every((k) => prev[k] === next[k]);
      if (unchanged) return prev;
      try { localStorage.setItem("qs_signal_first_seen", JSON.stringify(next)); } catch { /* quota */ }
      return next;
    });

    // Push active signals so NewsTab can fetch signal-linked news.
    // Guarded by a ref so re-pushing the same set does not loop with the
    // globalSignals update that this effect depends on.
    if (onData) {
      const key = active.map((sig) => sig.id).join(",");
      if (key !== pushedRef.current) {
        pushedRef.current = key;
        onData("activeSignals", active);
        // Breadth (active vs independent positions + IR multiplier) — same
        // change-cadence as activeSignals, so the existing guard keeps it
        // loop-safe. Additive: does not touch detection or the push above.
        const breadth = computeBreadth(active);
        onData("breadth", {
          activeCount: breadth.active,
          independentPositions: breadth.independent,
          irMultiplier: Math.sqrt(breadth.independent),
        });
      }
    }

    // ── Signal firing history (persistent) ──
    // Diff current active types vs the previous cycle: append an open entry on
    // activation (skip if one is already open for that type), stamp resolvedAt
    // on deactivation. Then publish the full history to globalSignals.
    const currentTypes = new Set(active.map((sig) => signalTypeOf(sig.id)));
    const prevTypes = prevTypesRef.current;
    let history = loadSignalHistory();
    let mutated = false;
    const nowIso = new Date().toISOString();

    for (const type of currentTypes) {
      if (!prevTypes.has(type) && !getOpenEntry(history, type)) {
        const sig = active.find((s) => signalTypeOf(s.id) === type);
        history.push({
          signalType: type,
          signalId: sig ? sig.id : type,
          firedAt: nowIso,
          resolvedAt: null,
        });
        mutated = true;
      }
    }
    for (const type of prevTypes) {
      if (!currentTypes.has(type)) {
        const open = getOpenEntry(history, type);
        if (open) {
          open.resolvedAt = nowIso;
          mutated = true;
        }
      }
    }
    if (mutated) history = saveSignalHistory(history);
    prevTypesRef.current = currentTypes;

    if (onData) {
      const historyJson = JSON.stringify(history);
      if (historyJson !== historyPushedRef.current) {
        historyPushedRef.current = historyJson;
        onData("signalHistory", history);
      }
    }
  }, [globalSignals, onData]);

  const activeCount = signals.filter((s) => s.id !== "none").length;
  const strongCount = signals.filter((s) => s.strength === "STRONG" || s.strength === "ELEVATED").length;

  // Sort applied to the card list only. STRENGTH mode leaves the existing
  // rule-order untouched (byte-identical, same reference). RELEVANCE mode — only
  // when portfolio exposures exist — sorts by descending relevance score, ties
  // broken by strength rank. Other consumers (summary strip, breadth, panels)
  // continue to read the unsorted `signals`.
  const exposuresAvailable = !!globalSignals?.portfolioExposures;
  const displaySignals =
    sortMode === "RELEVANCE" && exposuresAvailable
      ? [...signals].sort((a, b) => {
          const sb = computeRelevanceScore(b, globalSignals.portfolioExposures);
          const sa = computeRelevanceScore(a, globalSignals.portfolioExposures);
          if (sb !== sa) return sb - sa;
          return (STRENGTH_RANK[b.strength] || 0) - (STRENGTH_RANK[a.strength] || 0);
        })
      : signals;

  return (
    <div>
      {/* Board header */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: 12,
      }}>
        <div>
          <div style={{
            fontSize: 15,
            fontWeight: 700,
            letterSpacing: "0.12em",
            color: C.text,
            marginBottom: 3,
          }}>
            SIGNAL CONVERGENCE BOARD
          </div>
          <div style={{ fontSize: 11, color: C.textDim, lineHeight: 1.6 }}>
            {lastScan
              ? `${activeCount} active signal${activeCount !== 1 ? "s" : ""} · ${strongCount > 0 ? `${strongCount} strong/elevated · ` : ""}Last scan: ${lastScan} · Scan #${scanCount}`
              : "Load data on any tab to begin populating signals"}
          </div>
        </div>

        {/* Strength legend */}
        <div style={{
          display: "flex",
          gap: 5,
          alignItems: "center",
          flexShrink: 0,
          flexWrap: "wrap",
          justifyContent: "flex-end",
        }}>
          {/* Sort toggle — only when portfolio exposures are available */}
          {exposuresAvailable && (
            <span
              onClick={toggleSort}
              title="Toggle signal ordering"
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: C.textMid,
                letterSpacing: "0.05em",
                padding: "1px 8px",
                background: C.panelAlt,
                borderRadius: 2,
                border: `1px solid ${C.border}`,
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              {sortMode === "STRENGTH" ? "SORT BY RELEVANCE" : "SORT BY STRENGTH"}
            </span>
          )}
          {Object.entries(STRENGTH_COLOR).filter(([k]) => k !== "NONE").map(([label, color]) => (
            <span key={label} style={{
              fontSize: 11,
              color,
              padding: "1px 6px",
              background: color + "15",
              borderRadius: 2,
              border: `1px solid ${color}33`,
            }}>
              {label}
            </span>
          ))}
        </div>
      </div>

      {/* Active signals summary strip — only shown when signals exist */}
      {activeCount > 0 && (
        <div style={{
          display: "flex",
          gap: 5,
          flexWrap: "wrap",
          marginBottom: 10,
          padding: "7px 10px",
          background: C.panelAlt,
          border: `1px solid ${C.border}`,
          borderRadius: 3,
        }}>
          <span style={{ fontSize: 11, color: C.textDim, marginRight: 4 }}>ACTIVE:</span>
          {signals.filter((s) => s.id !== "none").map((sig) => (
            <span key={sig.id} style={{
              fontSize: 11,
              color: sig.color,
              padding: "1px 6px",
              background: sig.color + "15",
              borderRadius: 2,
              border: `1px solid ${sig.color}33`,
            }}>
              {sig.icon} {sig.title}
            </span>
          ))}
        </div>
      )}

      {/* Factor Framework breadth bar — active vs independent positions */}
      {activeCount > 0 && (
        <BreadthBar activeSignals={signals.filter((s) => s.id !== "none")} />
      )}

      {/* Signal cards */}
      {displaySignals.map((sig) => (
        <SignalCard
          key={sig.id}
          sig={sig}
          anthropicKey={keys?.anthropicKey}
          globalSignals={globalSignals}
          detectedAt={firstSeen?.[sig.id] ?? null}
        />
      ))}

      {/* Phase 3 educational panels — additive, self-isolating (error-bounded) */}
      <FactorBacktest
        activeSignals={signals.filter((s) => s.id !== "none")}
        globalSignals={globalSignals}
      />
      <IREducationPanel
        activeSignals={signals.filter((s) => s.id !== "none")}
        independentPositions={computeBreadth(signals.filter((s) => s.id !== "none")).independent}
        portfolioExposures={globalSignals?.portfolioExposures}
      />

      {/* Footer note */}
      <div style={{
        fontSize: 11,
        color: C.textDim,
        marginTop: 8,
        lineHeight: 1.6,
        padding: "6px 0",
        borderTop: `1px solid ${C.border}`,
      }}>
        Signal board updates automatically as tab data loads.{" "}
        <span style={{ color: "#FFD700", fontWeight: 700 }}>
          Signals are descriptive confluences, not predictions or recommendations.
          Not a recommendation — consult a licensed financial advisor for decisions.
        </span>
      </div>
    </div>
  );
}
